import ThreadEditorVM from 'components/editors/thread/thread';
import CommentsView from 'enums/comments/views';
import { wccModules } from 'enums/wccModules';
import { CustomElementConfig } from 'extensions/knockout/bindings/customElement';
import { inject, injectable } from 'inversify';
import { Observable, PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import device from 'managers/device';
import DiscussionTranslationManager from 'managers/discussion/translation';
import { IWCCStorageManager } from 'managers/iStorage';
import localStorageManager from 'managers/localStorage';
import SimpleTopicManager from 'managers/simpleTopic';
import { TopicManager } from 'managers/topic';
import ActivityFormManager, { ActivityFormEditorConfig } from 'managers/topic/form';
import { EffectsContainer } from 'mixins/withEffects';
import CommentsFilters, { CommentsFiltersConfig } from 'models/commentsFilters';
import { Topic } from 'models/topic';
import UserActivityTracker from 'tasks/userActivityTracker';
import SimpleTopicsManager from '../../../managers/discussion/simpleTopics';
import { ISignalRCurrentUserSessionManager } from '../../../managers/signalR/sessions/currentUser.interfaces';
import CommentsManager from '../../../managers/topic/comments';
import { TopicSummaryVMConfig } from '../../topic/summary/summary';
import { TopicsSliderVMConfig } from '../topics/slider/slider';
import './activity.html';
import './activity.scss';
import ActivityNavigationManager from './managers/navigation';
import TaskActivityViewLinksManager from './managers/viewLinks';
import { TaskActivityViewLinkModel } from './viewlink';

const PollAnchor = require('components/polls/anchor/model');

const { isStaging, forceMobileUI, isCommunity } = settings;

components
    .registerLocal('task-activity-viewlink', () => require('./viewlink'))
    .registerLocal('task-activity-mobile-editors-toolbar', () => require('./mobileEditorsToolbar'))
    .registerLocal('task-activity-clear-todo-button', () => require('./clearToDoButton'))
    .registerLocal('task-activity-post-button', () => require('./postButton'))
    .registerLocal('task-activity-ideation-tools', () => require('./ideation/tools/tools'))
    .registerLocal('task-activity-ideation-stage-message', () => require('./ideation/stageMessage/stageMessage'))
    .registerLocal('task-activity-ideation-votes-counter', () => require('./ideation/votesCounter/votesCounter'));

components.preload('task-topics-slider', 'topic-summary', 'topic-comments-filters', 'topic-comments', 'translation-checkbox');

export interface ActivtityVMConfig {
    taskId: SubscribableOrNullableValue<string>
    groupId?: SubscribableOrNullableValue<string>
    topicId: Observable<string | undefined>
    view: Observable<string | undefined>
}

@injectable()
export default class ActivityVM {
    private simpleTopicsManager: Subscribable<SimpleTopicsManager | undefined>

    private viewLinksManager: TaskActivityViewLinksManager
    private navigationManager: ActivityNavigationManager

    labels = labels;
    element: HTMLElement

    editorToolsContainer = ko.observable<HTMLElement>();

    pollAnchor = new PollAnchor();
    commentsFilters = new CommentsFilters();

    taskId: Subscribable<string | undefined>
    topicId: Observable<string | undefined>
    layout: Subscribable<string>
    autoJumpMessage: Subscribable<string | undefined>

    mainFormConfig: Subscribable<ActivityFormEditorConfig | undefined>
    mainForm = ko.observable<ThreadEditorVM>();

    secondaryFormConfig: Subscribable<ActivityFormEditorConfig | undefined>
    customFormConfig: Subscribable<CustomElementConfig | undefined>

    views: Subscribable<Array<TaskActivityViewLinkModel>>
    commentsView: Subscribable<CommentsView | undefined>

    isRegular: Subscribable<boolean>
    isReady: Subscribable<boolean>
    isLoading: Subscribable<boolean>
    isIdeation: Subscribable<boolean>
    allowCommentsFiltering: Subscribable<boolean>
    allowTranslations: Subscribable<boolean>
    isMandatoryProbesShown: Subscribable<boolean>
    isPollsShown: Subscribable<boolean>
    isMobile: Subscribable<boolean>
    isDesktop: Subscribable<boolean>
    isToDoView: Subscribable<boolean>
    isAutoJumpMessageShown: Subscribable<boolean>
    areCommentsAllowed: Subscribable<boolean>
    sidebarShown: Subscribable<boolean>
    commentsTableShown: Subscribable<boolean>
    commentsGridShown: Subscribable<boolean>
    topMenuShown: Subscribable<boolean>
    isObserver: Subscribable<boolean>
    isMaterialUIEnabled: PureComputed<boolean>

    topicsSliderConfig: TopicsSliderVMConfig
    topicSummaryConfig: TopicSummaryVMConfig

    constructor(
        @inject(wccModules.componentConfig) config: ActivtityVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.signalRCurrentUserSession) currentUserSession: ISignalRCurrentUserSessionManager,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) private effects: EffectsContainer
    ) {
        this.element = <HTMLElement>info.element;

        this.taskId = ko.flattenComputed(config.taskId);
        const groupId = ko.flattenComputed(config.groupId);
        const topicId = ko.isWritableObservable(config.topicId) ? config.topicId as Observable<string | undefined> : ko.observable(ko.unwrap(config.topicId));
        const viewName = ko.isWritableObservable(config.view) ? config.view as Observable<string | undefined> : ko.observable(ko.unwrap(config.view));

        this.topicId = topicId;

        this.isMobile = ko.pureComputed(() => device.isMobile() || forceMobileUI());
        this.isDesktop = ko.pureComputed(() => !this.isMobile());

        const userManager = storage.get(CurrentUserManager, { discussionId: this.taskId });
        const loadingUser = userManager.pluck(m => m.loading, true);
        const user = userManager.pluck(m => m.person);
        this.isRegular = user.pluck(u => u.isRegular, false);
        const isModeratorOrObserver = user.pluck(u => u.isModeratorOrObserver, false);
        this.isObserver = user.pluck(u => u.isObserver, false);

        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        const loadingTopic = topicManager.pluck(m => m.loading, true);
        const topic = topicManager.pluck(m => m.topic);
        this.isIdeation = topic.pluck(t => t.isIdeation, false);
        const isExternalSurvey = topic.pluck(t => t.isExternalSurvey, false);
        const isTopicAnswered = topic.pluck(t => t.isAnswered, false);

        const privateTopicAutoJumpType = this.isRegular.pluck(isRegular => isRegular ? topic.pluck(t => t.privateTopicAutoJumpType, enums.PrivateTopicAutoJumpTypes.DoNothing.value) : enums.PrivateTopicAutoJumpTypes.DoNothing.value);
        const privateTopicAutoJumpPostsNumber = topic.pluck(t => t.privateTopicAutoJumpPostsNumber, 0);
        this.autoJumpMessage = topic.pluck(t => t.privateTopicAutoJumpMessage);

        const userCommentsManager = storage.get(CommentsManager, { topicId: this.topicId, view: CommentsView.mine });
        const userComments = userCommentsManager.pluck(m => m.realComments, []);

        this.isAutoJumpMessageShown = privateTopicAutoJumpType.pluck(autoJumpType => {
            if (autoJumpType == enums.PrivateTopicAutoJumpTypes.ShowMessage.value)
                return privateTopicAutoJumpPostsNumber.is(target => target > 0 && userComments().length >= target);

            return false;
        });

        this.simpleTopicsManager = storage.get(SimpleTopicsManager, { discussionId: this.taskId });

        const simpleTopicManager = storage.get(SimpleTopicManager, { topicId: this.topicId, searchString: this.commentsFilters.searchString, peopleTagsIDs: this.commentsFilters.peopleTagsIDs });
        const simpleTopic = simpleTopicManager.pluck(m => m.topic);

        const translationManager = storage.get(DiscussionTranslationManager, { discussionId: this.taskId });
        const allowTranslations = translationManager.pluck('isAllowed', false);
        this.allowTranslations = ko.pureComputed(() => allowTranslations());

        const editorConfigManager = this.effects.register(new ActivityFormManager(this.taskId, this.topicId, { isMobile: this.isMobile, toolsContainer: this.editorToolsContainer }));

        this.viewLinksManager = effects.register(new TaskActivityViewLinksManager(viewName, user, simpleTopic, editorConfigManager, this.commentsFilters));
        const activeViewLink = this.viewLinksManager.viewLinks.find(l => l.isActive());
        this.views = this.viewLinksManager.viewLinks;
        this.commentsView = activeViewLink.pluck(l => l.commentsView);
        this.areCommentsAllowed = this.commentsView.isNotNull();
        this.sidebarShown = activeViewLink.pluck(l => l.sidebar, true);
        this.isToDoView = this.commentsView.is(CommentsView.todo);

        this.mainFormConfig = activeViewLink.pluck(l => l.mainForm);
        this.secondaryFormConfig = activeViewLink.pluck(l => l.secondaryForm);
        this.customFormConfig = activeViewLink.pluck(l => l.customForm);

        this.layout = topic.mapSingle(topic => this.getLayout(topic));
        this.commentsTableShown = ko.pureComputed(() => this.areCommentsAllowed() && isExternalSurvey() && isModeratorOrObserver());
        this.commentsGridShown = ko.pureComputed(() => this.areCommentsAllowed() && !this.commentsTableShown());        
        this.topMenuShown = ko.pureComputed(() => this.isDesktop() && !this.commentsTableShown() /*&& !this.isObserver()*/);
        this.isMaterialUIEnabled = ko.pureComputed(() => WCC.Settings.MaterialUIEnabled ?? false);

        this.navigationManager = effects.register(new ActivityNavigationManager({
            taskId: this.taskId,
            topicId: this.topicId,
            navigate: this.activateTopicInternal.bind(this),
            storage: storage
        }));

        this.allowCommentsFiltering = ko.pureComputed(() => this.isDesktop());
        this.isPollsShown = ko.pureComputed(() => isCommunity());

        this.isReady = ko.pureComputed<boolean>(() => {
            return !loadingUser() &&
                !loadingTopic() &&
                this.navigationManager.isReady();
        }).extend({ deferred: true });

        this.isLoading = this.isReady.not();

        this.isMandatoryProbesShown = ko.pureComputed(() => this.isReady() && !isStaging && this.isRegular());

        this.topicsSliderConfig = {
            taskId: this.taskId,
            groupId: groupId,
            selectedTopicId: this.topicId,
            filters: this.commentsFilters,
            theme: 'small-rounded',
            onTopicClick: this.activateTopic
        }

        this.topicSummaryConfig = {
            topicId: this.topicId,
            topicBox: false,
            spinner: false
        }

        effects.register([
            topicId.subscribeNotNull(topicId => this.navigationManager.activateTopic(topicId))
        ]);

        if (ko.isWritableObservable(config.topicId))
            effects.register(() => this.topicId.subscribe(config.topicId));

        effects.register(topicId => {
            if (topicId != undefined)
                return storage.installTask(UserActivityTracker, { topicId });
        }, [this.topicId]);

        effects.register(topicId => {
            if (topicId != undefined)
                return currentUserSession.assignToTopic(topicId);
        }, [this.topicId]);
        
        effects.register(topicId => {
            if (topicId != undefined) {
                $(document).off('paste').on('paste', (e: any) => {
                    try {
                        const pastedText = e.originalEvent.clipboardData.getData('text');
                        if(!pastedText || !pastedText.trim())
                            return;
                        
                        WCC.API.apiRequest({
                            url: 'topics/' + topicId + '/people/pasteHistory',
                            method: 'POST',
                            data: { PastedText: e.originalEvent.clipboardData.getData('text') }
                        });
                    }
                    catch(e) {
                        console.warn(e);
                    }
                });
            }
        }, [this.topicId]);

        effects.register(privateTopicAutoJumpType => {
            if (privateTopicAutoJumpType == enums.PrivateTopicAutoJumpTypes.Auto.value) {
                return isTopicAnswered.subscribe(isTopicAnswered => {
                    if (isTopicAnswered)
                        _.delay(() =>this.jumpToNextTopic(), 1000);
                });
            }
        }, [privateTopicAutoJumpType]);

        effects.register((taskId, user) => {
            if (taskId != undefined && user != undefined) {
                const bucket = localStorageManager.commentsFilters(taskId, <string>user.personId());
                const bucketContent = bucket.get();

                if (bucketContent != undefined)
                    this.commentsFilters.update(<CommentsFiltersConfig>JSON.parse(bucketContent));

                const state = ko.pureComputed(() => this.commentsFilters.toJson());

                return state.subscribe(state => bucket.set(JSON.stringify(state)));
            }
        }, [this.taskId, user]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'task-activity': true,
                'task-activity--mobile': device.isMobile,
                'task-activity--with-poll': this.pollAnchor.isActive,                
                'task-activity--with-topmenu': this.topMenuShown
            }
        });
    }

    activateTopic = (topicId: string) => {
        this.navigationManager.activateTopic(topicId);
    }

    jumpToNextTopic = async () => {
        const simpleTopicsManager = await this.simpleTopicsManager.whenNotNull();
        await simpleTopicsManager.loading.when(loading => !loading);

        const simpleTopics = simpleTopicsManager.topics();
        const nextTopicId = simpleTopics.find(topic => topic.shouldBeAnswered())?.id() ??
            simpleTopics.find(topic => !topic.isAnswered())?.id();

        if (nextTopicId != undefined)
            this.activateTopicInternal(nextTopicId);
    }

    private getLayout(topic?: Topic): string {
        if (topic != undefined) {
            if (topic.isInformationOnlyTopic()
                || (topic.isExternalSurvey() && this.isRegular()))
                return 'wcc-components-task-activity-info';
        }

        return 'wcc-components-task-activity-general';
    }

    private activateTopicInternal(id: string) {
        this.topicId(id);
        this.viewLinksManager.toDefaultView();

        $('html, body').scrollTop(0);
    }
}